import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  styled,
  CircularProgress,
  Alert,
  IconButton,
  Fade,
} from '@mui/material';
import {
  CalendarMonth,
  AccessTime,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { getAvailableSlots, getProviderInfo } from '../services/api';
import { WorkingPlan } from '../types';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: '2px solid transparent',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#9c4f96',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(156, 79, 150, 0.15)',
  },
}));

const SelectedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: '2px solid #9c4f96',
  backgroundColor: 'rgba(156, 79, 150, 0.05)',
}));

interface DateTimePickerProps {
  selectedDate: string;
  selectedTime: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
}) => {
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errorSlots, setErrorSlots] = useState<string | null>(null);
  const [workingPlan, setWorkingPlan] = useState<WorkingPlan | null>(null);
  const [loadingWorkingPlan, setLoadingWorkingPlan] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [showScrollHint, setShowScrollHint] = useState(true);

  // Buscar working plan do provider
  useEffect(() => {
    const fetchWorkingPlan = async () => {
      setLoadingWorkingPlan(true);
      try {
        const providerInfo = await getProviderInfo(2);
        if (providerInfo && providerInfo.settings && providerInfo.settings.workingPlan) {
          setWorkingPlan(providerInfo.settings.workingPlan);
          console.log('Working plan carregado:', providerInfo.settings.workingPlan);
        }
      } catch (error) {
        console.error('Erro ao buscar horários de trabalho:', error);
      } finally {
        setLoadingWorkingPlan(false);
      }
    };

    fetchWorkingPlan();
  }, []);

  // Buscar horários disponíveis quando uma data for selecionada
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!selectedDate) {
        setAvailableSlots([]);
        return;
      }

      setLoadingSlots(true);
      setErrorSlots(null);
      
      try {
        console.log('Buscando horários para:', selectedDate);
        const slots = await getAvailableSlots(1, selectedDate, 2);
        console.log('Horários recebidos:', slots);
        
        setAvailableSlots(slots);
        
        if (slots.length === 0) {
          setErrorSlots('Não há horários disponíveis para esta data. Por favor, escolha outra data.');
        }
      } catch (error) {
        console.error('Erro ao buscar horários:', error);
        setErrorSlots('Erro ao buscar horários disponíveis. Tente novamente.');
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailableSlots();
  }, [selectedDate]);

  // Verificar se um dia da semana está disponível no working plan
  const isDayAvailable = (dayOfWeek: number): boolean => {
    if (!workingPlan) return false;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek] as keyof WorkingPlan;
    
    return workingPlan[dayName] !== null;
  };

  // Gerar próximos dias úteis baseado no working plan
  const getNextBusinessDays = () => {
    if (loadingWorkingPlan || !workingPlan) {
      return [];
    }

    const days = [];
    const today = new Date();
    // Normalizar a data para evitar problemas de fuso horário
    today.setHours(0, 0, 0, 0);
    
    let currentDay = 0;
    let count = 0;

    while (count < 14 && currentDay < 60) {
      const date = new Date(today);
      date.setDate(today.getDate() + currentDay);
      
      const dayOfWeek = date.getDay();
      
      if (isDayAvailable(dayOfWeek)) {
        // Formatar data manualmente para evitar conversão de timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        days.push({
          date: dateString, // Usar formato manual em vez de toISOString()
          dayName: date.toLocaleDateString('pt-BR', { weekday: 'short' }),
          dayNumber: date.getDate(),
          monthName: date.toLocaleDateString('pt-BR', { month: 'short' }),
          fullDate: date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: 'long',
            year: 'numeric',
            weekday: 'long'
          }),
        });
        count++;
      }
      currentDay++;
    }
    return days;
  };

  // Formatar horários para exibição
  const formatTimeSlots = (slots: string[]) => {
    // Filtrar slots válidos antes de processar
    const validSlots = slots.filter(slot => slot && typeof slot === 'string');
    
    return validSlots.map((time) => {
      try {
        // Remover segundos se existirem (HH:mm:ss -> HH:mm)
        const timeParts = time.split(':');
        
        if (timeParts.length < 2) {
          console.warn('Formato de horário inválido:', time);
          return null;
        }
        
        const formattedTime = `${timeParts[0]}:${timeParts[1]}`;
        
        const hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        
        if (isNaN(hours)) {
          console.warn('Hora inválida:', time);
          return null;
        }
        
        const isPM = hours >= 12;
        const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        const period = hours < 12 ? 'Manhã' : 'Tarde';
        
        return {
          time: formattedTime,
          label: `${String(displayHour).padStart(2, '0')}:${minutes}`,
          period,
          sortOrder: hours * 60 + parseInt(minutes),
        };
      } catch (error) {
        console.error('Erro ao formatar horário:', time, error);
        return null;
      }
    }).filter((slot): slot is NonNullable<typeof slot> => slot !== null)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const timeSlots = formatTimeSlots(availableSlots);
  const businessDays = getNextBusinessDays();

  // Verificar estado do scroll
  const checkScrollState = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 4);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollState);
      checkScrollState();
      return () => el.removeEventListener('scroll', checkScrollState);
    }
  }, [businessDays]);

  // Esconder hint após primeira interação
  useEffect(() => {
    const el = scrollRef.current;
    const hideHint = () => setShowScrollHint(false);
    if (el) {
      el.addEventListener('scroll', hideHint, { once: true });
      el.addEventListener('touchstart', hideHint, { once: true });
    }
  }, []);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -220, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 220, behavior: 'smooth' });
  };

  if (loadingWorkingPlan) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#9c4f96', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Carregando dias disponíveis...
        </Typography>
      </Box>
    );
  }

  if (!workingPlan) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Não foi possível carregar os horários de atendimento. Tente novamente mais tarde.
      </Alert>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Seleção de Data */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CalendarMonth sx={{ mr: 1, color: '#9c4f96' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Escolha a Data
          </Typography>
        </Box>

        {/* Hint de scroll */}
        <Fade in={showScrollHint && businessDays.length > 4}>
          <Typography
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#9c4f96',
              mb: 2,
              opacity: 0.8,
            }}
          >
            <ChevronLeft fontSize="small" />
            Deslize para ver mais datas disponíveis
            <ChevronRight fontSize="small" />
          </Typography>
        </Fade>

        {businessDays.length === 0 ? (
          <Alert severity="warning">
            Não há dias disponíveis para agendamento no momento.
          </Alert>
        ) : (
          <Box sx={{ position: 'relative' }}>

            {/* Botão esquerda */}
            <Fade in={canScrollLeft}>
              <IconButton
                onClick={scrollLeft}
                size="small"
                sx={{
                  position: 'absolute',
                  left: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  border: '1px solid #e0e0e0',
                  '&:hover': { bgcolor: '#f5f5f5', borderColor: '#9c4f96' },
                }}
              >
                <ChevronLeft sx={{ color: '#9c4f96' }} />
              </IconButton>
            </Fade>

            {/* Gradiente esquerda */}
            <Fade in={canScrollLeft}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 12,
                  width: 40,
                  background: 'linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0))',
                  zIndex: 1,
                  pointerEvents: 'none',
                  borderRadius: '8px 0 0 8px',
                }}
              />
            </Fade>

            {/* Lista de datas */}
            <Box
              ref={scrollRef}
              sx={{
                display: 'flex',
                overflowX: 'auto',
                gap: 2,
                pb: 3,
                pt: 1,
                px: 1,
                mx: -1,
                scrollbarWidth: 'thin',
                scrollbarColor: '#9c4f96 #f0e6f0',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f0e6f0',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#9c4f96',
                  borderRadius: 4,
                },
              }}
            >
              {businessDays.map((day) => {
                const isSelected = selectedDate === day.date;
                const Component = isSelected ? SelectedPaper : StyledPaper;

                return (
                  <Component
                    key={day.date}
                    onClick={() => {
                      onDateChange(day.date);
                      setShowScrollHint(false);
                    }}
                    sx={{
                      minWidth: 100,
                      textAlign: 'center',
                      position: 'relative',
                      flexShrink: 0,
                    }}
                  >
                    {isSelected && (
                      <CheckCircle
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#9c4f96',
                          fontSize: 20,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: isSelected ? '#9c4f96' : '#666',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      {day.dayName}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: isSelected ? '#9c4f96' : '#333',
                        fontWeight: 700,
                        my: 1,
                      }}
                    >
                      {day.dayNumber}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isSelected ? '#9c4f96' : '#666',
                        textTransform: 'capitalize',
                      }}
                    >
                      {day.monthName}
                    </Typography>
                  </Component>
                );
              })}
            </Box>

            {/* Gradiente direita */}
            <Fade in={canScrollRight}>
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 12,
                  width: 60,
                  background: 'linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0))',
                  zIndex: 1,
                  pointerEvents: 'none',
                  borderRadius: '0 8px 8px 0',
                }}
              />
            </Fade>

            {/* Botão direita */}
            <Fade in={canScrollRight}>
              <IconButton
                onClick={scrollRight}
                size="small"
                sx={{
                  position: 'absolute',
                  right: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  border: '1px solid #e0e0e0',
                  '&:hover': { bgcolor: '#f5f5f5', borderColor: '#9c4f96' },
                }}
              >
                <ChevronRight sx={{ color: '#9c4f96' }} />
              </IconButton>
            </Fade>

            {/* Indicador de progresso (bolinhas) */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
              {canScrollLeft && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#9c4f96' }} />
              )}
              <Box sx={{ width: 20, height: 6, borderRadius: 3, bgcolor: '#9c4f96' }} />
              {canScrollRight && (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#c9a0c6' }} />
              )}
            </Box>

          </Box>
        )}

        {selectedDate && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Data selecionada:{' '}
              <strong>
                {businessDays.find((d) => d.date === selectedDate)?.fullDate}
              </strong>
            </Typography>
          </Box>
        )}
      </Box>

      {/* Seleção de Horário */}
      {selectedDate && (
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccessTime sx={{ mr: 1, color: '#9c4f96' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Escolha o Horário
            </Typography>
          </Box>

          {loadingSlots ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#9c4f96', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Carregando horários disponíveis...
              </Typography>
            </Box>
          ) : errorSlots ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {errorSlots}
            </Alert>
          ) : timeSlots.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Não há horários disponíveis para esta data. Tente outra data.
            </Alert>
          ) : (
            <Box sx={{ pb: 2 }}>
              {/* Manhã */}
              {timeSlots.filter((slot) => slot.period === 'Manhã').length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: '#666', fontWeight: 600 }}
                  >
                    Período da Manhã
                  </Typography>
                  <Grid container spacing={2}>
                    {timeSlots
                      .filter((slot) => slot.period === 'Manhã')
                      .map((slot) => (
                        <Grid item xs={6} sm={4} md={3} key={slot.time}>
                          <Chip
                            label={slot.label}
                            onClick={() => onTimeChange(slot.time)}
                            color={selectedTime === slot.time ? 'primary' : 'default'}
                            icon={
                              selectedTime === slot.time ? (
                                <CheckCircle sx={{ fontSize: 18 }} />
                              ) : undefined
                            }
                            sx={{
                              width: '100%',
                              height: 48,
                              fontSize: '1rem',
                              fontWeight: selectedTime === slot.time ? 700 : 500,
                              bgcolor:
                                selectedTime === slot.time
                                  ? '#9c4f96'
                                  : 'transparent',
                              color:
                                selectedTime === slot.time ? 'white' : '#333',
                              border: '2px solid',
                              borderColor:
                                selectedTime === slot.time ? '#9c4f96' : '#e0e0e0',
                              '&:hover': {
                                bgcolor:
                                  selectedTime === slot.time
                                    ? '#8a4785'
                                    : 'rgba(156, 79, 150, 0.1)',
                                borderColor: '#9c4f96',
                              },
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}

              {/* Tarde */}
              {timeSlots.filter((slot) => slot.period === 'Tarde').length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: '#666', fontWeight: 600 }}
                  >
                    Período da Tarde
                  </Typography>
                  <Grid container spacing={2}>
                    {timeSlots
                      .filter((slot) => slot.period === 'Tarde')
                      .map((slot) => (
                        <Grid item xs={6} sm={4} md={3} key={slot.time}>
                          <Chip
                            label={slot.label}
                            onClick={() => onTimeChange(slot.time)}
                            color={selectedTime === slot.time ? 'primary' : 'default'}
                            icon={
                              selectedTime === slot.time ? (
                                <CheckCircle sx={{ fontSize: 18 }} />
                              ) : undefined
                            }
                            sx={{
                              width: '100%',
                              height: 48,
                              fontSize: '1rem',
                              fontWeight: selectedTime === slot.time ? 700 : 500,
                              bgcolor:
                                selectedTime === slot.time
                                  ? '#9c4f96'
                                  : 'transparent',
                              color:
                                selectedTime === slot.time ? 'white' : '#333',
                              border: '2px solid',
                              borderColor:
                                selectedTime === slot.time ? '#9c4f96' : '#e0e0e0',
                              '&:hover': {
                                bgcolor:
                                  selectedTime === slot.time
                                    ? '#8a4785'
                                    : 'rgba(156, 79, 150, 0.1)',
                                borderColor: '#9c4f96',
                              },
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}

              {selectedTime && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Horário selecionado: <strong>{selectedTime}</strong>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DateTimePicker;
